import type { OnInit } from '@angular/core';
import { ChangeDetectionStrategy, Component, inject, Input, output } from '@angular/core';
import type { FormControlStatus, UntypedFormGroup } from '@angular/forms';
import { UntypedFormBuilder, Validators, ReactiveFormsModule } from '@angular/forms';

import { map } from 'rxjs/operators';

import { UserSelectContainerComponent } from '@core/account-ui';
import type { BillingContactFormFields } from '@core/billing-types';
import { emailValidator, phoneNumberValidatorFn } from '@core/common-legacy';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import {
  TaggerComponent,
  DividerComponent,
  FormErrorDirective,
  PhoneInputComponent,
  InputComponent,
  RtTooltip,
  IconHelpComponent,
  LabelComponent,
  FormFieldComponent,
} from '@core/user-interface-legacy';

import { BillingContactFormTranslationsToken } from './billing-contact-form-translations';

@Component({
  selector: 'rt-billing-contact-form',
  templateUrl: './billing-contact-form.component.html',
  styleUrls: ['./billing-contact-form.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    FormFieldComponent,
    LabelComponent,
    IconHelpComponent,
    RtTooltip,
    UserSelectContainerComponent,
    InputComponent,
    PhoneInputComponent,
    FormErrorDirective,
    DividerComponent,
    TaggerComponent,
    TranslatePipe,
  ],
})
export class BillingContactFormComponent implements OnInit {
  protected readonly t = inject(BillingContactFormTranslationsToken);
  public readonly phoneErrors: { [key: string]: string } = {
    invalidNumber: this.i18n.translate(this.t.errorPhoneInvalid),
    maxlength: this.i18n.translate(this.t.errorPhoneLength),
  };
  public form: UntypedFormGroup;
  public countryCode: string;

  @Input() public formValue: BillingContactFormFields & { countryCode?: string };
  @Input() public currentUserId: number | string;
  @Input() public fields: Array<keyof BillingContactFormFields> = [
    'fullName',
    'email',
    'phone',
    'invoiceEmails',
    'primaryContactId',
  ];

  public valueChanges = output<BillingContactFormFields>();
  public statusChanges = output<FormControlStatus>();

  constructor(
    private fb: UntypedFormBuilder,
    private i18n: I18nService,
  ) {}

  public ngOnInit(): void {
    this.form = this.fb.group({});

    if (this.fields.includes('fullName')) {
      this.form.addControl('fullName', this.fb.control(this.formValue.fullName));
    }

    if (this.fields.includes('email')) {
      this.form.addControl(
        'email',
        this.fb.control(this.formValue.email, [Validators.required, emailValidator]),
      );
    }

    if (this.fields.includes('phone')) {
      this.form.addControl('phone', this.fb.control(this.formValue.phone, phoneNumberValidatorFn));
    }

    if (this.fields.includes('primaryContactId')) {
      const initialContactId = this.formValue.primaryContactId || this.currentUserId;
      this.form.addControl(
        'primaryContactId',
        this.fb.control(initialContactId, [Validators.required]),
      );
    }

    if (this.fields.includes('invoiceEmails')) {
      this.form.addControl(
        'invoiceEmails',
        this.fb.control(this.mapToTaggerValue(this.formValue.invoiceEmails || [])),
      );
    }

    this.subscribeValueChanges();
    this.form.statusChanges.subscribe(status => this.statusChanges.emit(status));
  }

  private subscribeValueChanges(): void {
    this.form.valueChanges
      .pipe(
        map(formValue => {
          if (!this.fields.includes('invoiceEmails')) return formValue;

          return {
            ...formValue,
            invoiceEmails: this.mapFromTaggerValue(formValue.invoiceEmails),
          };
        }),
      )
      .subscribe(value => this.valueChanges.emit(value));
  }

  private mapFromTaggerValue(items: Array<{ isValid: boolean; email: string }>): string[] {
    return items.filter(({ isValid }) => isValid).map(({ email }) => email);
  }

  private mapToTaggerValue(emails: string[]): Array<{ isValid: boolean; email: string }> {
    return emails.map(email => ({ email, isValid: true }));
  }
}
